import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CircularTimer } from '../casino/CasinoTimer'
import { cardSrc } from '../casino/cards/cardUtils'
import useModalEnter from '../../hooks/useModalEnter'
import { getVirtualEventMedia, getCricketVCards } from '../../services/api'
import { SPORTS_STREAM_BASE } from '../../config/api'

/* T5 XI / T10 XI video column — reference admin /admin/virtualcricket/…
 * (capture: docs/ui/admin-ref/virtualcricket/t10.ref.*.html):
 *
 *   <div class="casino-video mt-0">
 *     <div class="video-box-container"><div class="video-box"><iframe></div></div>
 *     <div class="casino-video-cards [hide-cards]">
 *       <div class="casino-cards-shuffle"><i class="fas fa-grip-lines-vertical"></div>
 *       <div class="casino-video-cards-container"><div><span><img></span></div> ×6</div>
 *     </div>
 *     <div class="casino-timer"><div class="base-timer">…</div></div>
 *     <div class="casino-video-right-icons">rules (button) + sync (not clickable)</div>
 *   </div>
 *
 * Cards: one slot per ball, "1" = not bowled (card back); a wide and its
 * re-ball share a slot ("JSS,ASS") and both show. `hide-cards` while nothing
 * is dealt — the reference capture has it on an all-backs over and drops it
 * once a card turns. Same feed and polling as the player app's
 * VirtualCricketDetails (useXICards / useVirtualEventMedia). */

const POLL_MS = 500
// The provider page sets up NanoPlayer with `style: {width:'auto'}`, which
// pins its player to 640×360 whatever the iframe size (desktop: small video
// in a black box; phone: cropped). The reference's own player fills the box.
// So the iframe is laid out at the player's size and scaled to the box —
// both are 16:9, so it fills exactly.
// shortcut: assumes the provider keeps 640×360; drop this when we serve our own player page.
const PLAYER_W = 640
const PLAYER_H = 360

// Scale + the CSS border-radius divided by the scale, so the corners still
// paint at the stylesheet's size after the transform.
function useFitToBox(boxRef, dep) {
  const [fit, setFit] = useState(null)
  useEffect(() => {
    const box = boxRef.current
    if (!box) return undefined
    const measure = () => {
      const f = box.querySelector('iframe')
      if (!f || !box.clientWidth) return
      f.style.borderRadius = '' // read the stylesheet's value, not ours
      const s = box.clientWidth / PLAYER_W
      const r = getComputedStyle(f).borderRadius.replace(/[\d.]+px/g, (v) => `${parseFloat(v) / s}px`)
      setFit({ s, r })
    }
    const ro = new ResizeObserver(measure)
    ro.observe(box)
    measure()
    return () => ro.disconnect()
  }, [boxRef, dep])
  return fit
}

function useXICards(gmid, gtype) {
  const [data, setData] = useState({ card: '', ft: 0, lt: 0 })
  useEffect(() => {
    setData({ card: '', ft: 0, lt: 0 })
    if (!gmid || !gtype) return undefined
    let alive = true
    const poll = async () => {
      try {
        const d = await getCricketVCards(gmid, gtype)
        if (alive && d) setData({ card: d.card || '', ft: Number(d.ft) || 0, lt: Number(d.lt) || 0 })
      } catch { /* keep the last cards — one failed poll is not a reset */ }
    }
    poll()
    const iv = setInterval(poll, POLL_MS)
    return () => { alive = false; clearInterval(iv) }
  }, [gmid, gtype])
  return data
}

function useEventStream(gmid, etid) {
  // undefined while in flight so the iframe mounts once, with the right url
  const [stream, setStream] = useState(undefined)
  useEffect(() => {
    let alive = true
    setStream(undefined)
    getVirtualEventMedia(gmid, etid)
      .then((m) => { if (alive) setStream(m?.stream || null) })
      .catch(() => { if (alive) setStream(null) })
    return () => { alive = false }
  }, [gmid, etid])
  return stream
}

function RulesModal({ onClose }) {
  const { entered, backdropClass, onBackdropClick } = useModalEnter(onClose)
  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => document.body.classList.remove('modal-open')
  }, [])
  return createPortal(
    <>
      <div
        id="rules-modal"
        role="dialog"
        aria-modal="true"
        className={`modal fade casino-rules${entered ? ' show' : ''}`}
        style={{ display: 'block' }}
        onClick={onBackdropClick}
      >
        <div className="modal-dialog modal-md">
          <div className="modal-content" tabIndex="-1">
            <header className="modal-header">
              <h5 className="modal-title">Rules</h5>
              <button type="button" aria-label="Close" className="close" onClick={onClose}>&times;</button>
            </header>
            <div className="modal-body">
              <img src="/admin/assets/casino/rules/cricketv2.jpg" className="img-fluid" alt="Rules" />
            </div>
          </div>
        </div>
      </div>
      <div className={backdropClass}></div>
    </>,
    document.body
  )
}

export default function VirtualXIVideo({ gmid, etid, gtype }) {
  const stream = useEventStream(gmid, etid)
  const { card, ft, lt } = useXICards(gmid, gtype)
  const [showRules, setShowRules] = useState(false)
  const boxRef = useRef(null)
  const fit = useFitToBox(boxRef, stream)

  // local countdown between polls, re-anchored only on a real jump (new ball)
  const endsAtRef = useRef(0)
  const [timeLeft, setTimeLeft] = useState(0)
  useEffect(() => {
    const localMs = Math.max(0, endsAtRef.current - Date.now())
    if (Math.abs(lt * 1000 - localMs) > 2500) endsAtRef.current = Date.now() + lt * 1000
  }, [lt])
  useEffect(() => {
    const iv = setInterval(() => setTimeLeft(Math.max(0, Math.ceil((endsAtRef.current - Date.now()) / 1000))), 250)
    return () => clearInterval(iv)
  }, [])

  const slots = card ? card.split('|') : []
  while (slots.length < 6) slots.push('1')
  const codes = (slot) => slot.split(',').map((c) => c.trim()).filter(Boolean)
  const allBacks = slots.every((s) => codes(s).every((c) => c === '1' || c === '0'))

  return (
    <div className="casino-video mt-0">
      <div className="video-box-container">
        <div className="video-box" ref={boxRef}>
          {stream !== undefined && (
            <iframe
              key={gmid}
              src={stream || `${SPORTS_STREAM_BASE}${gmid}`}
              title="Virtual Cricket"
              allow="autoplay"
              allowFullScreen
              style={fit && stream ? { width: PLAYER_W, height: PLAYER_H, transform: `scale(${fit.s})`, transformOrigin: '0 0', borderRadius: fit.r } : undefined}
            ></iframe>
          )}
        </div>
      </div>{' '}
      <div className={`casino-video-cards${allBacks ? ' hide-cards' : ''}`}>
        <div className="casino-cards-shuffle">
          <i className="fas fa-grip-lines-vertical"></i>
        </div>{' '}
        <div className="casino-video-cards-container">
          {slots.map((slot, i) => (
            <div key={i}>
              {/* a wide + its re-ball: one <span><img></span> each, side by side */}
              {(codes(slot).length ? codes(slot) : ['1']).map((c, j) => (
                <span key={j}><img src={cardSrc(c)} alt="" /></span>
              ))}
            </div>
          ))}
        </div>
      </div>{' '}
      <CircularTimer timeLeft={timeLeft} fullTime={ft || 30} />{' '}
      <div className="casino-video-right-icons">
        <div title="Rules" className="casino-video-rules-icon" role="button" tabIndex={0} onClick={() => setShowRules(true)}>
          <i className="fas fa-info-circle"></i>
        </div>{' '}
        <div className="casino-video-rules-icon">
          <i className="fas fa-sync-alt"></i>
        </div>
      </div>
      {showRules && <RulesModal onClose={() => setShowRules(false)} />}
    </div>
  )
}
